import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Shield, Download, Upload, FileText, Link as LinkIcon } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ChatBox from '../components/Chat/ChatBox';
import { ordersAPI } from '../api';

const SuperAdminOrderDetail = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get('token');

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  // Create a fake Super Admin user for chat
  const superAdminUser = {
    id: 'super-admin',
    name: 'Admin',
    role: 'engineer', // Use engineer role to access chat
    email: 'admin@gt.com'
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    loadOrder();
  }, [orderId, token]);

  const loadOrder = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/superadmin/orders/${orderId}`,
        {
          headers: {
            'X-Super-Admin-Token': token
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to load order');
      
      const data = await response.json();
      setOrder(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger la commande",
        variant: "destructive"
      });
      navigate(`/super-admin?token=${token}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUploadFile = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      await ordersAPI.uploadLog(orderId, selectedFile);
      toast({ title: "Fichier uploadé avec succès" });
      setSelectedFile(null);
      loadOrder();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'uploader le fichier",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleAddLink = async () => {
    if (!linkUrl.trim()) return;

    setUploading(true);
    try {
      await ordersAPI.addLogLink(orderId, linkUrl);
      toast({ title: "Lien ajouté avec succès" });
      setLinkUrl('');
      loadOrder();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter le lien",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (logId, version) => {
    try {
      const blob = await ordersAPI.downloadLog(orderId, logId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order_${orderId}_${version}.bin`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <p className="text-white">Chargement...</p>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="min-h-screen bg-[#111111] py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          className="mb-6 text-[#A6A6A6] hover:text-white"
          onClick={() => navigate(`/super-admin?token=${token}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour Super Admin
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-6 w-6 text-[#C00000]" />
          <h1 className="text-3xl font-bold text-white">
            Super Admin - Commande {order.order_id}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Order Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white">Détails de la commande</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[#A6A6A6] text-sm">Client</p>
                      <p className="text-white font-semibold">{order.client_name}</p>
                      <p className="text-[#A6A6A6] text-sm">{order.client_email}</p>
                    </div>
                    <div>
                      <p className="text-[#A6A6A6] text-sm">Statut</p>
                      <Badge>{order.status}</Badge>
                    </div>
                  </div>

                  <div>
                    <p className="text-[#A6A6A6] text-sm mb-2">Véhicule</p>
                    <p className="text-white">
                      {order.vehicle_info.brand} {order.vehicle_info.model} {order.vehicle_info.engine}
                    </p>
                  </div>

                  <div>
                    <p className="text-[#A6A6A6] text-sm">Service</p>
                    <p className="text-white">{order.service}</p>
                  </div>

                  <div>
                    <p className="text-[#A6A6A6] text-sm">Prix</p>
                    <p className="text-white font-bold text-xl">{order.price}€</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Upload Section */}
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Upload className="h-5 w-5 mr-2 text-[#C00000]" />
                  Uploader un fichier modifié (Super Admin)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-[#A6A6A6]">Fichier .bin modifié</Label>
                  <Input
                    type="file"
                    onChange={handleFileSelect}
                    className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                    accept=".bin,.log,.txt,.csv,.pdf"
                  />
                </div>
                <Button
                  onClick={handleUploadFile}
                  disabled={!selectedFile || uploading}
                  className="bg-[#C00000] hover:bg-[#C00000]/90 w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Upload...' : 'Uploader'}
                </Button>

                <div className="border-t border-[#A6A6A6]/20 pt-4">
                  <Label className="text-[#A6A6A6]">Ou ajouter un lien</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="https://wetransfer.com/..."
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                    />
                    <Button
                      onClick={handleAddLink}
                      disabled={!linkUrl.trim() || uploading}
                      className="bg-[#C00000] hover:bg-[#C00000]/90"
                    >
                      <LinkIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Files */}
            {order.logs && order.logs.length > 0 && (
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white">Fichiers modifiés</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {order.logs.map((log) => (
                      <div key={log.id} className="flex items-center justify-between bg-[#111111] p-3 rounded">
                        <div>
                          <span className="text-white font-semibold">{log.version}</span>
                          <span className="text-[#A6A6A6] text-sm ml-3">
                            {log.type === 'file' ? log.filename : log.url}
                          </span>
                        </div>
                        {log.type === 'file' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[#C00000]"
                            onClick={() => handleDownload(log.id, log.version)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Chat */}
          <div className="lg:col-span-1">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white text-sm">
                  💬 Chat (en tant qu'Admin)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ChatBox 
                  orderId={orderId}
                  userRole={superAdminUser.role}
                  user={superAdminUser}
                  embedded={true}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminOrderDetail;
